/**
 * Admin panel oRPC helper.
 *
 * Admin procedures live in the oRPC router at `/rpc/admin.*` (Track 4.P + 4.R).
 * This module wraps `fetch` with:
 *   1. oRPC procedure-name → path conversion (dots → slashes, prefixed with /rpc/).
 *   2. oRPC body envelope: `{ json: input }`.
 *   3. `credentials: "include"` so the session cookie rides along.
 *   4. `X-CSRF-Token` read from `getCsrfHeader()` — echoed on every mutating
 *      request per the double-submit pattern.
 *      `getCsrfHeader()` reads the `__Host-csrf` cookie (or bare `csrf` on
 *      `http://` dev origins).
 *
 * Architecture refs:
 *   §4.25 — Security baselines. CSRF double-submit.
 *   packages/client-core/src/auth/csrf.ts — canonical cookie reader.
 *   packages/client-core/src/api/index.ts — reference for CSRF attach pattern.
 *   apps/api/src/index.ts line 178 — csrfMiddleware() applied to /rpc/*.
 *
 * Option B chosen (see Wave B Group 9 task): the admin procedures use the
 * oRPC wire format. We keep a thin shared helper and inject CSRF here so all
 * five admin components can import a single, correct implementation.
 */

import { getCsrfHeader } from "@proof/client-core/auth";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AdminRpcOptions {
  /** Override fetch — used in Vitest to mock network calls. */
  fetch?: typeof globalThis.fetch;
}

// ─── Helper ───────────────────────────────────────────────────────────────────

/**
 * Call an admin oRPC procedure with session credentials and CSRF header.
 *
 * @param procedure — dot-separated procedure name, e.g. `admin.waitlist.list`
 * @param input     — JSON-serialisable input (wrapped in `{ json: input }`)
 * @param opts      — optional: override `fetch` for testing
 *
 * The oRPC path is derived by replacing dots with slashes and prepending
 * `/rpc/`: `admin.waitlist.list` → `/rpc/admin/waitlist/list`.
 *
 * Throws a `AdminRpcError` on non-2xx responses (caller decides how to
 * surface the error — no toast wiring here).
 */
export async function rpcPost<T = unknown>(
  procedure: string,
  input: unknown,
  opts?: AdminRpcOptions,
): Promise<T> {
  const path = "/rpc/" + procedure.split(".").join("/");

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  // Attach CSRF double-submit token on every call (all oRPC mutations are POSTs).
  const csrfToken = getCsrfHeader();
  if (csrfToken) {
    headers["X-CSRF-Token"] = csrfToken;
  }

  const f = opts?.fetch ?? globalThis.fetch;
  const resp = await f(path, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({ json: input }),
  });

  if (!resp.ok) {
    const text = await resp.text().catch(() => "(unreadable)");
    throw new AdminRpcError(resp.status, text.slice(0, 200));
  }

  const body = (await resp.json()) as { json: T };
  return body.json;
}

// ─── Maintenance backfills ──────────────────────────────────────────────────
//
// Operator one-shot backfill procedures (both assertAdmin-gated, both take no
// input). Thin typed wrappers over rpcPost so the Maintenance panel imports a
// named call rather than a stringly-typed procedure name. Idempotent server-side
// — safe to re-run (see apps/api/src/routes/admin/{lineage,stats}.ts).

/** Return shape of `admin.lineage.backfill` (public-lineage index rebuild). */
export interface LineageBackfillResult {
  scanned: number;
  written: number;
  deleted: number;
}

/** Return shape of `admin.stats.backfill` (public-recipe-stats fill). */
export interface StatsBackfillResult {
  scanned: number;
  created: number;
}

/** Rebuild the public-lineage index from every public recipe. Idempotent. */
export function backfillLineage(opts?: AdminRpcOptions): Promise<LineageBackfillResult> {
  return rpcPost<LineageBackfillResult>("admin.lineage.backfill", {}, opts);
}

/** Create missing public-recipe-stats rows for dormant public recipes. Idempotent. */
export function backfillRecipeStats(opts?: AdminRpcOptions): Promise<StatsBackfillResult> {
  return rpcPost<StatsBackfillResult>("admin.stats.backfill", {}, opts);
}

// ─── Deep analytics ───────────────────────────────────────────────────────────
//
// Typed wrappers over the `admin.analytics.*` procedures. The shapes below
// mirror apps/api/src/routes/admin/analytics.ts — keep them in step when that
// file changes.
//
// Three DISTINCT unavailable states travel over this boundary and must never be
// conflated in the UI:
//   instrumentation_pending — the D1 read path is live; the emitters are not.
//   not_configured          — this environment has no Cloudflare API token, so
//                             the Analytics Engine deep queries cannot run.
//   query_failed            — a token IS configured and the engine call errored
//                             (bad scope, transport, timeout, non-JSON body).
//                             Rendering this as an empty window hides the fault.
// Every D1-backed chart renders regardless of either.

/** A daily series as parallel arrays, oldest day first. */
export interface DailySeries {
  days: string[];
  values: number[];
}

/** A section whose emitters have not shipped yet. */
export interface SectionPending {
  available: false;
  reason: "instrumentation_pending";
}

/** Return shape of `admin.analytics.overview`. */
export interface AnalyticsOverview {
  generatedAt: number;
  daysBack: number;
  days: string[];
  funnel: {
    waitlist: Array<{ status: string; count: number }>;
    waitlistTotal: number;
    usersTotal: number;
    milestones:
      SectionPending | { available: true; counts: Array<{ event: string; count: number }> };
  };
  signups: { series: DailySeries; last7d: number; total: number };
  activity: {
    available: true;
    hasData: boolean;
    dau: DailySeries;
    wau: DailySeries;
    latestDau: number;
    latestWau: number;
    stickiness: number | null;
  };
  retention: {
    available: true;
    hasData: boolean;
    maxWeekIndex: number;
    cohorts: Array<{ week: string; size: number; retained: number[] }>;
    w1Milestone: SectionPending | { available: true; count: number };
  };
  activation:
    | SectionPending
    | {
        available: true;
        cohorts: Array<{ week: string; size: number; activated: number; pct: number }>;
      };
  waitlistDepth: {
    total: DailySeries;
    byStatus: Array<{ status: string; series: DailySeries }>;
    current: number;
    snapshotDays: number;
  };
  unitCosts: {
    available: true;
    hasData: boolean;
    emailSent: DailySeries;
    mediaBytes: DailySeries;
  };
  pwa:
    | SectionPending
    | {
        available: true;
        installedTotal: number;
        displayModes: Array<{ detail: string; count: number }>;
      };
}

/** Return shape of `admin.analytics.snapshotWaitlistDepth`. */
export interface WaitlistDepthSnapshot {
  day: string;
  statuses: Array<{ status: string; count: number }>;
  total: number;
}

/** The named Analytics Engine queries the server whitelist accepts. */
export type AeQueryName = "events_daily" | "display_mode_split" | "media_bytes_daily";

/** Return shape of `admin.analytics.aeSeries`. */
export type AeSeriesResult =
  | { available: false; reason: "not_configured" | "query_failed" }
  | {
      available: true;
      name: AeQueryName;
      rows: Array<Record<string, string | number | null>>;
      rowsRead: number | null;
    };

/** Read every D1-backed analytics chart in one call. */
export function fetchAnalyticsOverview(
  daysBack: number,
  opts?: AdminRpcOptions,
): Promise<AnalyticsOverview> {
  return rpcPost<AnalyticsOverview>("admin.analytics.overview", { daysBack }, opts);
}

/**
 * Take a waitlist-depth reading now.
 *
 * Production accrues this series from a daily scheduled job; dev has no cron
 * triggers by design, so this is the equivalent operator action. Idempotent per
 * UTC day — the server write replaces the day's rows rather than adding to them.
 */
export function snapshotWaitlistDepth(opts?: AdminRpcOptions): Promise<WaitlistDepthSnapshot> {
  return rpcPost<WaitlistDepthSnapshot>("admin.analytics.snapshotWaitlistDepth", {}, opts);
}

/**
 * Run one whitelisted Analytics Engine query by NAME.
 *
 * There is no SQL on this side of the boundary: the caller picks a name and the
 * server holds the statement. Resolves to `{available:false}` — never rejects —
 * when the environment has no Cloudflare API token.
 */
export function fetchAeSeries(name: AeQueryName, opts?: AdminRpcOptions): Promise<AeSeriesResult> {
  return rpcPost<AeSeriesResult>("admin.analytics.aeSeries", { name }, opts);
}

// ─── Maintenance one-shots ──────────────────────────────────────────────────
//
// Two more procedures that shipped without a UI. Both are assertAdmin-gated and
// both are on-demand precisely because dev has no cron triggers — a weekly
// digest wired only to a cron would be unverifiable on the one environment that
// gets verified.

/** Return shape of `admin.groups.digestRun` (weekly group-digest send). */
export interface DigestRunResult {
  sent: number;
  skipped: number;
  failed: number;
}

/** Return shape of `admin.assistant.ping` (synthetic inference smoke test). */
export interface AssistantPingResult {
  ok: boolean;
  model?: string;
  latencyMs?: number;
  usage?: { promptTokens?: number; completionTokens?: number; totalTokens?: number };
  sample?: string;
}

/** Send the weekly group digest to everyone opted in. */
export function runGroupDigest(opts?: AdminRpcOptions): Promise<DigestRunResult> {
  return rpcPost<DigestRunResult>("admin.groups.digestRun", {}, opts);
}

/**
 * Run a fixed synthetic prompt through the assistant path.
 *
 * Synthetic fixture only — no user text reaches the model, and the call does
 * not consume a user's operation budget. It still honours the environment kill
 * switch, so a disabled environment answers with a refusal rather than a send.
 */
export function pingAssistant(opts?: AdminRpcOptions): Promise<AssistantPingResult> {
  return rpcPost<AssistantPingResult>("admin.assistant.ping", {}, opts);
}

// ─── Error type ───────────────────────────────────────────────────────────────

export class AdminRpcError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "AdminRpcError";
    this.status = status;
  }
}
